import { APP_BASE_HREF, DATE_PIPE_DEFAULT_OPTIONS, formatNumber, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpBackend, HttpClient, HttpClientModule } from '@angular/common/http';
import localeDE from '@angular/common/locales/de-BE';
import localeEN from '@angular/common/locales/en-GB';
import localeFR from '@angular/common/locales/fr-BE';
import localeBE from '@angular/common/locales/nl-BE';
import 'moment/locale/nl'; 
import 'moment/locale/fr'; 
import 'moment/locale/de';
import { ErrorHandler, InjectionToken, LOCALE_ID, NgModule, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { EffectsModule } from '@ngrx/effects';
import { RouterStateSerializer, StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { CommonComponentsModule, DateOnlyInterceptor, DateUtils, Settings } from 'common-components';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthInterceptor } from './auth/interceptors/auth-interceptor';
import { CoreModule } from './core/core.module';
import { baseHrefFactory } from './i18n/base-href.factory';
import { i18nInterceptor } from './i18n/i18n.interceptor';
import { translateLoaderFactory } from './i18n/translate-loader.factory';
import { LogConfigServiceLoaderFactory } from './logging/public-log-config.service';
import { initialState, reducers } from './reducers';
import { SharedModule } from './shared/shared.module';
import { AppRouterStateSerializer } from './shared/utils/app.routerstate';
import { GlobalErrorHandler } from './shared/utils/global-error-handler';
import { LocalizeService } from './shared/utils/localize.service';

registerLocaleData(localeBE);
registerLocaleData(localeDE);
registerLocaleData(localeFR);
registerLocaleData(localeEN);

// exporting the settings function like this is required for AOT compilation to work
export function commonComponentsModuleSettings(): any {
    return {
        // empty settings for now, logging is using the default console logger atm
    };
}

const I18N_ASSETS_FOLDERS = new InjectionToken<string[]>('i18n assets folders for the module.');

const angularModules = [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule
];

const ngrxModules = [
    StoreModule.forRoot(reducers, {
        metaReducers: [],
        initialState,
        runtimeChecks: {strictStateImmutability: true, strictActionImmutability: true}
    }),
    StoreRouterConnectingModule.forRoot({ stateKey: 'router' }),
    StoreDevtoolsModule.instrument({
        maxAge: 25, // Retains last 25 states
        logOnly: environment.production // Restrict extension to log-only mode
    }),
    EffectsModule.forRoot([])
];

const appCoreModules = [
    CommonComponentsModule.forRoot(commonComponentsModuleSettings),
    SharedModule,
    CoreModule.forRoot(),
    TranslateModule.forRoot({
        loader: { provide: TranslateLoader, useFactory: translateLoaderFactory, deps: [HttpBackend, I18N_ASSETS_FOLDERS] }
    })
];

export function ensureLocale(): string {
  let locale = Intl.NumberFormat().resolvedOptions().locale;
  try {
    // If locale data is missing, this will throw
    formatNumber(1, locale);
    return locale;
  } catch {
    try {
      let newLocale = navigator.language;
      formatNumber(1, newLocale);
      return newLocale;
    } catch {
        return 'nl-BE';  // fallback
    }
  }
}

@NgModule({
    declarations: [
        AppComponent
    ],
    imports: [
        angularModules,
        ngrxModules,
        appCoreModules,
        // the application main routes should be the last ones registered because the 'match all unmatched' route is registered here
        AppRoutingModule
    ],
    bootstrap: [AppComponent],
    providers: [
        provideZoneChangeDetection(),
        {provide: APP_BASE_HREF, useFactory: baseHrefFactory, deps: [LocalizeService]},
        {
            provide: I18N_ASSETS_FOLDERS,
            useValue: [
                'app',
                'app/core',
                'flows',
                'flows/aanvraag',
                'flows/check-tool',
                'flows/product-tool',
                'app/shared',
                'portal',
                'app/shared/cookiebeleid'
            ]
        },
        {provide: ErrorHandler, useClass: GlobalErrorHandler},
        {provide: RouterStateSerializer, useClass: AppRouterStateSerializer},
        {provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true},
        {provide: HTTP_INTERCEPTORS, useClass: i18nInterceptor, multi: true},
        {provide: HTTP_INTERCEPTORS, useClass: DateOnlyInterceptor, multi: true},
        provideAppInitializer(() => {
        const initializerFn = ((localize: LocalizeService) => () => localize.initializeTranslate())(inject(LocalizeService));
        return initializerFn();
      }),
        {provide: Settings, useFactory: LogConfigServiceLoaderFactory, deps: [HttpClient]},
        // This way to fetch the locale works better than navigator.language
        // Because it takes into account browser-specific settings to change the formatting
        // And not only the browser's language
        { provide: LOCALE_ID, useFactory:() => ensureLocale()},
        { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { timezone: 'Europe/Brussels' }}
    ]
})
export class AppModule {
}
